import type { AssignedAssignmentsFilters } from '@repo/types/assignments';

import { useGetAssignedAssignmentsInfinite } from '@repo/api/assignment/useGetAssignedAssignmentsInfinite';
import { useDebounceValue } from '@repo/hooks/useDebounceValue';
import { useInfiniteQueryResult } from '@repo/hooks/useInfiniteQueryResult';
import { useState } from 'react';

interface UseAssignedAssignmentsData {
  enabledAssignedAssignments?: boolean;
  groupId?: number;
}

export function useAssignedAssignmentsData(useAssignedAssignmentsData?: UseAssignedAssignmentsData) {
  const { enabledAssignedAssignments = true, groupId } = useAssignedAssignmentsData || {};
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
  const searchDebouncedValue: string | undefined = useDebounceValue(searchValue, 400);
  const [filterValue, setFilterValue] = useState<AssignedAssignmentsFilters | undefined>(undefined);

  const getAssignedAssignments = useGetAssignedAssignmentsInfinite({
    name: searchDebouncedValue,
    filter: filterValue,
    groupId,
    enabled: enabledAssignedAssignments,
  });

  const { data, isEmpty, noResults } = useInfiniteQueryResult(getAssignedAssignments.data, searchDebouncedValue);

  const next = async () => {
    await getAssignedAssignments.fetchNextPage();
  };

  return {
    state: {
      searchValue,
      filterValue,
      data,
      isEmpty,
      noResults,
      hasNextPage: getAssignedAssignments.hasNextPage,
      isFetching: getAssignedAssignments.isFetching,
      isError: getAssignedAssignments.isError,
      isLoading: getAssignedAssignments.isLoading,
      isPending: getAssignedAssignments.isPending,
    },
    actions: {
      next,
      setSearchValue,
      setFilterValue,
    },
  };
}
